<template>
  <header
    class="fixed top-0 left-0 right-0 z-50 transition-all duration-300 pt-[calc(0.75rem+var(--safe-top))] pb-3 lg:pt-2.5 lg:pb-2.5"
    :class="[
      scrolled ? 'bg-bochas-azul-oscuro shadow-lg' : 'lg:bg-black/10',
      isOpen && 'bg-bochas-azul-oscuro shadow-lg',
    ]"
  >
    <div class="flex items-center justify-between px-4 sm:px-6 lg:px-24">
      <router-link
        to="/"
        class="flex items-center gap-3"
        aria-label="Federación de Bochas Córdoba - Inicio"
        @click.prevent="handleLogoClick"
      >
        <div class="h-16 sm:h-16 lg:h-20 flex items-center justify-center">
          <IconFbochas
            name="federación"
            :size="180"
            :image-width="180"
            :image-height="45"
            class="lg:hidden"
          />
          <IconFbochas name="federación" :size="210" class="hidden lg:block" />
        </div>
      </router-link>

      <ul
        class="hidden items-center gap-8 text-lg font-medium text-white lg:flex"
      >
        <li v-for="link in links" :key="link.label">
          <router-link
            :to="link.to"
            class="relative transition hover:text-bochas-red"
            :class="isActive(link.to) ? 'text-white' : 'text-white/70'"
          >
            {{ link.label }}
            <span
              v-if="isActive(link.to)"
              class="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-bochas-red rounded-full"
            />
          </router-link>
        </li>
      </ul>

      <button
        class="lg:hidden text-white p-2 -mr-2"
        type="button"
        :aria-label="isOpen ? 'Cerrar menú' : 'Abrir menú'"
        :aria-expanded="isOpen"
        aria-controls="mobile-navigation"
        @click="toggleMenu"
      >
        <svg
          v-if="!isOpen"
          width="32"
          height="32"
          viewBox="0 0 30 30"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M0 4.375C0 3.3375 0.8375 2.5 1.875 2.5H21.875C22.9125 2.5 23.75 3.3375 23.75 4.375C23.75 5.4125 22.9125 6.25 21.875 6.25H1.875C0.8375 6.25 0 5.4125 0 4.375ZM21.875 22.5H1.875C0.8375 22.5 0 23.3375 0 24.375C0 25.4125 0.8375 26.25 1.875 26.25H21.875C22.9125 26.25 23.75 25.4125 23.75 24.375C23.75 23.3375 22.9125 22.5 21.875 22.5ZM28.125 12.5H8.125C7.0875 12.5 6.25 13.3375 6.25 14.375C6.25 15.4125 7.0875 16.25 8.125 16.25H28.125C29.1625 16.25 30 15.4125 30 14.375C30 13.3375 29.1625 12.5 28.125 12.5Z"
            fill="currentColor"
          />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>
    </div>

    <Transition name="menu">
      <nav
        v-if="isOpen"
        id="mobile-navigation"
        class="relative z-10 w-full rounded-b-lg bg-bochas-azul-oscuro px-4 pb-3 pt-2 sm:px-6 sm:pb-4 lg:hidden"
        aria-label="Navegación principal"
      >
        <ul class="mx-auto w-full max-w-3xl">
          <li
            v-for="(link, index) in links"
            :key="link.label"
            :class="index < links.length - 1 ? 'border-b border-white/35' : ''"
          >
            <router-link
              :to="link.to"
              class="flex min-h-12 items-center rounded px-2 py-3 text-base font-medium leading-tight text-white transition-colors hover:bg-white/5 active:bg-white/10 sm:min-h-14 sm:px-3 sm:text-lg md:text-xl"
              @click="closeMenu"
            >
              {{ link.label }}
            </router-link>
          </li>
        </ul>
      </nav>
    </Transition>
  </header>

  <Transition name="overlay">
    <button
      v-if="isOpen"
      class="fixed inset-0 z-40 cursor-default bg-black/70 lg:hidden"
      type="button"
      aria-label="Cerrar menú"
      @click="closeMenu"
    />
  </Transition>
</template>

<script setup>
import { computed, nextTick, ref, onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import IconFbochas from "../Icons/IconFbochas.vue";
import {
  crearSiteVisibility,
  obtenerSiteVisibility,
} from "@/services/siteSettings/siteVisibilityService";

const route = useRoute();
const router = useRouter();

const allLinks = [
  { label: "Inicio", to: "/" },
  { label: "Torneos", to: "/calendario", visibilityKey: "pages.calendario" },
  { label: "Resultados", to: "/resultados", visibilityKey: "pages.resultados" },
  {
    label: "La Federación",
    to: "/la-federacion",
    visibilityKey: "pages.federacion",
  },
  { label: "Novedades", to: "/noticias", visibilityKey: "pages.noticias" },
];
const visibility = ref(crearSiteVisibility());
const links = computed(() =>
  allLinks.filter(
    (link) => !link.visibilityKey || visibility.value[link.visibilityKey],
  ),
);

const scrolled = ref(false);
const isOpen = ref(false);

function isActive(to) {
  if (to === "/") return route.path === "/";
  return route.path.startsWith(to);
}

function handleScroll() {
  scrolled.value = window.scrollY > 30;
}

function toggleMenu() {
  isOpen.value = !isOpen.value;
}

function closeMenu() {
  isOpen.value = false;
}

async function handleLogoClick() {
  closeMenu();

  if (route.path !== "/") {
    await router.push("/");
    await nextTick();
  }

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: 0, left: 0, behavior: reduced ? "auto" : "smooth" });
}

function handleEscape(event) {
  if (event.key === "Escape") closeMenu();
}

watch(() => route.fullPath, closeMenu);

watch(isOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : "";
});

onMounted(async () => {
  try {
    visibility.value = await obtenerSiteVisibility();
  } catch (_) {}
  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("keydown", handleEscape);
  handleScroll();
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("keydown", handleEscape);
  document.body.style.overflow = "";
});
</script>

<style scoped>
.menu-enter-active,
.menu-leave-active {
  transition: opacity 180ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
  transform-origin: top;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-8px) scaleY(0.98);
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 180ms ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .menu-enter-active,
  .menu-leave-active,
  .overlay-enter-active,
  .overlay-leave-active {
    transition: none;
  }
}
</style>
